import React from "react";
import styled from "styled-components";

import { TestWrapper } from "@ui/Atoms";
import { Tabs, Tab } from "@ui/Molecules/Tabs";

interface Props {
  testId?: string;
  className?: string;
  currentPage: number;
  numberOfPages: number;
  translations: {
    nextText: string;
    previousText: string;
  };
  isMobile?: boolean;
  isTablet?: boolean;
  changePage: (value: number) => void;
}

const Pagination = (props: Props): React.ReactElement => {
  const { isTablet, numberOfPages, currentPage, changePage } = props;

  const createPagination = React.useCallback(() => {
    const tabs: React.ReactElement[] = [];
    const range = isTablet ? 1 : 2;
    const rangeMaxMin = isTablet ? 3 : 5;

    for (let i = 1; i <= numberOfPages; i++) {
      const isMaxMinPage = i === 1 || i === numberOfPages;
      const isInRange = currentPage - range <= i && i <= currentPage + range;
      const isInMaxMinRange =
        (currentPage <= rangeMaxMin && i <= rangeMaxMin) ||
        (currentPage >= numberOfPages - rangeMaxMin &&
          i >= numberOfPages - rangeMaxMin);

      if (isInRange || isInMaxMinRange || isMaxMinPage) {
        tabs.push(
          <TabStyled
            key={i}
            active={i === currentPage}
            onClick={() => changePage(i)}
          >
            {i}
          </TabStyled>,
        );
      } else if (i === 2 || i === numberOfPages - 1) {
        tabs.push(
          <TabStyled key={i} disabled>
            …
          </TabStyled>,
        );
      }
    }

    return tabs;
  }, [currentPage, isTablet, numberOfPages, changePage]);

  return (
    <TestWrapper testId={props.testId}>
      <div className={props.className}>
        <TabsStyled variant="buttons">
          <TabStyled
            disabled={currentPage === 1}
            onClick={() => changePage(currentPage - 1)}
          >
            {props.translations.previousText}
          </TabStyled>
          {!props.isMobile && createPagination()}
          <TabStyled
            disabled={currentPage === numberOfPages}
            onClick={() => changePage(currentPage + 1)}
          >
            {props.translations.nextText}
          </TabStyled>
        </TabsStyled>
      </div>
    </TestWrapper>
  );
};

export { Pagination };

const TabsStyled = styled(Tabs)`
  display: grid;
  grid-template-columns: 1fr 1fr;

  ${props => props.theme.breakpoints.tablet} {
    display: block;
  }
`;

const TabStyled = styled(Tab)`
  transition: none;
`;
